<template>
  <Loading v-if="props.loading" :data-loading="props.loading" />
  <div v-else class="card-chart-container" :data-loading="props.loading">
    <VueApexCharts
      :options="chart.chartOptions"
      :series="chart.series"
      width="100%"
      height="100%"
    />
  </div>
</template>

<script setup lang="ts">
import { formatNumber } from "@/utils";
import type { ApexOptions } from "apexcharts";
import _ from "lodash";
import { computed } from "vue";
import VueApexCharts from "vue3-apexcharts";
import Loading from "../Widgets/Loading.vue";

export interface ColumnChartProps {
  series: ApexAxisChartSeries;
  loading?: boolean;
  title: string;
  labels?: string[];
  direction?: "vertical" | "horizontal";
  stacked?: boolean;
  showLabels?: boolean;
}

const props = defineProps<ColumnChartProps>();

const chart = computed<{
  series: ApexAxisChartSeries;
  chartOptions: ApexOptions;
}>(() => ({
  series: props.series,
  chartOptions: {
    chart: {
      type: "bar",
      zoom: {
        enabled: false
      },
      stacked: props.stacked === true,
      fontFamily: "RobotoCustom"
    },
    title: {
      text: props.title,
      style: {
        fontFamily: "RobotoCustomSemiBold",
        fontWeight: 600,
        fontSize: "15px"
      }
    },
    plotOptions: {
      bar: {
        columnWidth: 30,
        horizontal: props.direction === "horizontal",
        dataLabels: {
          position: "top",
          total: {
            enabled: true,
            style: {
              fontSize: "13px",
              fontWeight: 900
            }
          }
        }
      }
    },
    stroke: {
      show: false,
      width: 0
    },
    dataLabels: {
      enabled: props.showLabels,
      background: {
        enabled: false
      },
      offsetY: -18,
      offsetX: 0.8,
      style: {
        colors: ["black"]
      },
      formatter(val: number, { dataPointIndex, seriesIndex }) {
        const data = props.series[seriesIndex].data[dataPointIndex];
        if (data && typeof data === "object" && "meta" in data) {
          return `${formatNumber(val, 1)} ${data?.meta?.unit || ""}`;
        } else {
          return `${formatNumber(val, 1)}`;
        }
      }
    },
    yaxis: {
      labels: {
        formatter(val) {
          return `${formatNumber(Number(val))}`;
        }
      }
    },
    xaxis: {
      categories: props.labels
    },
    tooltip: {
      intersect: false,
      x: {
        formatter(val, { dataPointIndex, seriesIndex }) {
          return (
            (props.series[seriesIndex].data[dataPointIndex] as any)?.x ??
            props.labels?.[dataPointIndex] ??
            val
          );
        }
      }
    }
  } as ApexOptions
}));
</script>
